// SPDX-License-Identifier: MIT
// Input sanitization helpers for Azure SAS and configuration related strings.

// Allowed characters for a SAS query segment (conservative whitelist)
// Alphanumerics plus set of URL safe characters and percent encoding symbols.
const SAS_ALLOWED = /^[A-Za-z0-9&%=:_?-]+$/;

export interface SanitizationResult {
  value: string;
  issues: string[];
}

export function sanitizeSasInput(
  raw: string,
  opts: { maxLength?: number } = {}
): SanitizationResult {
  const maxLength = opts.maxLength ?? 2048; // generous but capped
  let value = raw.trim();
  const issues: string[] = [];
  if (value.length > maxLength) {
    value = value.slice(0, maxLength);
    issues.push('TRUNCATED');
  }
  // Disallow line breaks & control chars
  if (/\r|\n|\t|\0/.test(value)) {
    value = value.replace(/[\r\n\t\0]/g, '');
    issues.push('CONTROL_CHARS_REMOVED');
  }
  // Basic script keyword guard (defense-in-depth, unlikely in SAS)
  if (/script:/i.test(value)) {
    issues.push('POTENTIAL_SCRIPT_PATTERN');
  }
  // If it's a full URL, isolate the query part for pattern check
  let queryPortion = value;
  try {
    if (value.startsWith('https://')) {
      const u = new URL(value);
      queryPortion = u.search.replace(/^\?/, '') || '';
    }
  } catch {
    // ignore parse errors; treat whole string
  }
  if (queryPortion && !SAS_ALLOWED.test(queryPortion)) {
    issues.push('DISALLOWED_CHARS');
  }
  return { value, issues };
}

export function hardenUrlEcho(url: string): string {
  // Remove fragment (#...) if any, though SAS should not rely on fragment.
  return url.split('#')[0];
}
